import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Client_ from "../../api/Client";
import { supabase } from "../../config/supabase";
import { notificationActions, NotificationState } from "../slices/notificationSlice";
import { registerNotificationAction, UNDO_WINDOW_MS } from "../../lib/notificationActions";
import { userActions } from "../slices/userSlice";
import { trackSignUp } from "../../analytics/mixpanel";
import { loadCurrentProfile } from "../profileSession";

export const useNotification = () => {
    const dispatch = useDispatch();

    const displayNotification = (notification: NotificationState) => {
        dispatch(notificationActions.addNotification(notification));
    };

    const addNotificationError = (message: string) => {
        dispatch(notificationActions.addNotification({
            type: "error",
            message,
        }));
    };

    const addNotification = (message: string) => {
        dispatch(notificationActions.addNotification({
            message,
        }));
    };

    const addNotificationWarning = (message: string) => {
        dispatch(notificationActions.addNotification({
            type: "warning",
            message,
        }));
    };


    const addNotificationSuccess = (message: string) => {
        dispatch(notificationActions.addNotification({
            type: "success",
            message,
        }));
    };

    /** A toast with an Undo button. The callback runs at most once. */
    const addNotificationUndo = (message: string, onUndo: () => void, timeout = UNDO_WINDOW_MS) => {
        dispatch(notificationActions.addNotification({
            type: 'info',
            message,
            timeout,
            actionLabel: 'Undo',
            actionId: registerNotificationAction(onUndo),
        }));
    };

    const clearNotification = () => {
        dispatch(notificationActions.clearNotification());
    };

    return { addNotification, addNotificationError, addNotificationWarning, addNotificationSuccess, addNotificationUndo, clearNotification } as const;
};

export const useUser = () => {
    const dispatch = useDispatch();
    const { addNotificationError, addNotificationSuccess } = useNotification()
    const navigate = useNavigate();

    const startLoading = () => {
        dispatch(userActions.setUserLoading());
    };

    const endLoading = () => {
        dispatch(userActions.setUserLoaded());
    };

    async function loginUser(email: string, password: string) {
        startLoading();
        const { error } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
        });
        if (error) {
            endLoading();
            const message = error.message.toLowerCase();
            if (message.includes('email not confirmed')) {
                addNotificationError('Your email is not confirmed. Check your inbox for the verification link.');
            } else if (message.includes('invalid login credentials')) {
                addNotificationError('The email or password is not correct. Check them and try again.');
            } else if (message.includes('missing email or phone')) {
                addNotificationError('Enter your email address.');
            } else if (message.includes('rate limit') || message.includes('too many')) {
                addNotificationError('Too many attempts. Wait a few minutes, then try again.');
            } else if (message.includes('fetch') || message.includes('network')) {
                addNotificationError('The log in did not reach the server. Check your connection and try again.');
            } else {
                addNotificationError('The log in did not finish. Try again.');
            }
            return;
        }
        // The auth-state subscriber loads the profile for the new session.
    }

    function registerUser(email: string, password: string, firstName: string) {
        startLoading();
        Client_.client_().register(email, password, firstName).then(({ err, res }) => {
            endLoading();
            if (err || !res?.success) {
                addNotificationError(err)
                return;
            } else {
                trackSignUp(res.result, 'email');
                addNotificationSuccess('Verification email sent. Please check your inbox and spam folder to complete registration.')
                navigate('/login')
            }
        });
    }

    function forgotPassword(email: string) {
        startLoading();
        Client_.client_().forgotPassword(email).then(({ err, res }) => {

            dispatch(userActions.setUser(res?.result))
            if (err || !res?.success) {
                addNotificationError(err)
                return;
            }
        });
    }

    function refreshProfile() {
        return loadCurrentProfile(dispatch);
    }

    function getUser() {
        return loadCurrentProfile(dispatch);
    }

    async function logoutUser() {
        // Clears the persisted session and stops the background refresh timer.
        await supabase.auth.signOut();
        // Still worth calling: the backend clears the Spotify cookie on logout.
        await Client_.client_().logout();
    }

    return {
        loginUser,
        registerUser,
        forgotPassword,
        logoutUser,
        getUser,
        refreshProfile
    }
}

const StoreActions = {
    null: null,
}

export default StoreActions;
